import React, { useState } from 'react';
import { AlertCircle, TrendingDown, Calendar as CalendarIcon, FileText, DollarSign, Users, AlertTriangle, CheckCircle } from 'lucide-react';
import FloorPlanHeatmap from './shared/FloorPlanHeatmap';

interface Site {
  id: string;
  name: string;
  utilization8wk: number;
  peakWed: number;
  vacancyStreak: number;
  ghostRate: number;
  nextLease: string;
}

interface DashboardProps {
  buildingData: any;
  onNavigate: (view: string) => void;
  segment?: 'commercial' | 'healthcare' | 'retail';
}

const Dashboard: React.FC<DashboardProps> = ({ buildingData, onNavigate, segment = 'commercial' }) => {
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [showLeaseWarRoom, setShowLeaseWarRoom] = useState(segment === 'commercial');
  const [warRoomSite, setWarRoomSite] = useState<string | null>(segment === 'commercial' ? 'riverside' : null);
  const [leaseRate, setLeaseRate] = useState(42);

  if (segment === 'commercial') {
    const sites: Site[] = [
      {
        id: 'hq-north',
        name: 'HQ North Tower',
        utilization8wk: 54,
        peakWed: 76,
        vacancyStreak: 8,
        ghostRate: 27,
        nextLease: 'Dec 2027'
      },
      {
        id: 'hq-south',
        name: 'HQ South Building',
        utilization8wk: 48,
        peakWed: 68,
        vacancyStreak: 22,
        ghostRate: 31,
        nextLease: 'Apr 2027'
      },
      {
        id: 'midtown',
        name: 'Midtown Office',
        utilization8wk: 62,
        peakWed: 84,
        vacancyStreak: 4,
        ghostRate: 18,
        nextLease: 'Sep 2028'
      },
      {
        id: 'riverside',
        name: 'Riverside Campus',
        utilization8wk: 38,
        peakWed: 52,
        vacancyStreak: 34,
        ghostRate: 35,
        nextLease: 'Jan 2027'
      }
    ];

    const sampleZones = [
      { id: 'north-wing', name: 'North Wing', x: 50, y: 50, width: 150, height: 120, occupancy: [0,8,24,32,28,30,26,22,18,12,8], capacity: 40 },
      { id: 'south-wing', name: 'South Wing', x: 250, y: 50, width: 150, height: 120, occupancy: [0,12,18,24,22,20,18,16,12,8,4], capacity: 30 },
      { id: 'conf-a', name: 'Conf A', x: 450, y: 50, width: 100, height: 120, occupancy: [0,0,8,12,10,8,6,4,0,0,0], capacity: 12 },
      { id: 'open-area', name: 'Open Area', x: 50, y: 220, width: 300, height: 180, occupancy: [0,18,42,56,52,48,44,38,28,20,12], capacity: 70 },
      { id: 'break-room', name: 'Break', x: 400, y: 220, width: 150, height: 80, occupancy: [0,4,8,12,16,18,14,10,6,4,2], capacity: 20 },
      { id: 'focus', name: 'Focus', x: 400, y: 340, width: 150, height: 60, occupancy: [0,6,8,10,8,8,6,4,2,0,0], capacity: 12 }
    ];

    const getUtilColor = (util: number) => {
      if (util < 40) return '#c33';
      if (util < 55) return '#8a7355';
      return '#0A9B9B';
    };

    const getVacancyColor = (weeks: number) => {
      if (weeks > 20) return '#c33';
      if (weeks > 10) return '#8a7355';
      return '#0A9B9B';
    };

    const getGhostColor = (rate: number) => {
      if (rate > 30) return '#c33';
      if (rate > 20) return '#8a7355';
      return '#0A9B9B';
    };

    const urgentSites = sites.filter(s => s.vacancyStreak > 15 && s.nextLease.includes('2027'));

    return (
      <div className="p-6" style={{ maxWidth: '1600px', margin: '0 auto' }}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
            Commercial Real Estate
          </h1>
          <p style={{ color: 'var(--sca-muted)' }}>
            Portfolio scoreboard. Sample building data, illustrative
          </p>
        </div>

        {/* Headline Numbers */}
        <div className="grid grid-cols-2 gap-4 mb-6" style={{ maxWidth: '600px' }}>
          <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown size={18} style={{ color: '#c33' }} />
              <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Reclaimable Band</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: 'var(--sca-ink)' }}>
              ~24K sq ft
            </div>
            <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
              from sustained vacancy zones
            </div>
          </div>

          <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={18} style={{ color: '#c33' }} />
              <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Sites Needing Decision</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: 'var(--sca-ink)' }}>
              {urgentSites.length}
            </div>
            <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
              lease within 12 mo + high vacancy
            </div>
          </div>
        </div>

        {/* Portfolio Matrix Hero */}
        <div className="rounded-lg border p-6 mb-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Portfolio Matrix
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--sca-border)' }}>
                  <th className="text-left py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Site / Floor</th>
                  <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Util (8wk)</th>
                  <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Peak Wed</th>
                  <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Vacancy Streak</th>
                  <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Ghost Rate</th>
                  <th className="text-left py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Next Lease</th>
                  <th className="text-center py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>War Room</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr
                    key={site.id}
                    onClick={() => setSelectedSite(site.id === selectedSite ? null : site.id)}
                    className="cursor-pointer transition-colors"
                    style={{
                      borderBottom: '1px solid var(--sca-border)',
                      backgroundColor: selectedSite === site.id ? 'var(--sca-hover)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedSite !== site.id) {
                        e.currentTarget.style.backgroundColor = 'var(--sca-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedSite !== site.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <td className="py-3 px-4 font-medium" style={{ color: 'var(--sca-ink)' }}>
                      {site.name}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-semibold" style={{ color: getUtilColor(site.utilization8wk) }}>
                        {site.utilization8wk}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-semibold" style={{ color: getUtilColor(site.peakWed) }}>
                        {site.peakWed}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-semibold" style={{ color: getVacancyColor(site.vacancyStreak) }}>
                        {site.vacancyStreak} wk
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-semibold" style={{ color: getGhostColor(site.ghostRate) }}>
                        {site.ghostRate}%
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span style={{ color: 'var(--sca-ink)' }}>{site.nextLease}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {site.nextLease.includes('2027') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setWarRoomSite(site.id);
                            setShowLeaseWarRoom(true);
                          }}
                          className="px-3 py-1 text-xs rounded transition-colors inline-flex items-center gap-1"
                          style={{
                            backgroundColor: 'var(--sca-teal)',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <FileText size={14} />
                          Open
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-xs" style={{ color: 'var(--sca-muted)' }}>
            Click row to view floor snapshot. Sites with 2027 leases show "Open" button for lease-event war room.
          </div>
        </div>

        {/* Lease-Event War Room */}
        {showLeaseWarRoom && warRoomSite && (
          <div className="rounded-lg border p-6 mb-6" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>LEASE-EVENT WAR ROOM</div>
                <h2 className="text-xl font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  {sites.find(s => s.id === warRoomSite)?.name}: Board Pack
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--sca-muted)' }}>
                  Lease event: {sites.find(s => s.id === warRoomSite)?.nextLease} (9-18 month window)
                </p>
              </div>
              <button
                onClick={() => {
                  setShowLeaseWarRoom(false);
                  setWarRoomSite(null);
                }}
                className="text-sm px-3 py-1 rounded"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid var(--sca-border)',
                  color: 'var(--sca-ink)',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>

            {/* Peak-Proof Capacity */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="rounded-lg border p-4" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Users size={16} style={{ color: 'var(--sca-teal)' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>Peak-Proof Capacity</span>
                </div>
                <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>252 seats</div>
                <div className="text-xs mb-2" style={{ color: 'var(--sca-muted)' }}>vs 198 peak Wed (20% buffer)</div>
                <div className="text-xs" style={{ color: '#0A9B9B' }}>
                  <CheckCircle size={12} style={{ display: 'inline', marginRight: 4 }} />
                  Can exit Floor 5 without risk
                </div>
              </div>

              <div className="rounded-lg border p-4" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign size={16} style={{ color: 'var(--sca-teal)' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>Reclaimable Value</span>
                </div>
                <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
                  ${(8500 * leaseRate / 1000).toFixed(0)}K/year
                </div>
                <div className="text-xs mb-2" style={{ color: 'var(--sca-muted)' }}>8,500 sq ft at ${leaseRate}/sq ft</div>
                <div className="mt-2">
                  <label className="text-xs" style={{ color: 'var(--sca-muted)' }}>Adjust lease rate:</label>
                  <input
                    type="number"
                    value={leaseRate}
                    onChange={(e) => setLeaseRate(parseInt(e.target.value) || 42)}
                    className="w-full mt-1 px-2 py-1 text-sm rounded border"
                    style={{ borderColor: 'var(--sca-border)' }}
                  />
                </div>
              </div>

              <div className="rounded-lg border p-4" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} style={{ color: '#8a7355' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>Residual Risk</span>
                </div>
                <div className="text-2xl font-bold mb-1" style={{ color: '#8a7355' }}>Low</div>
                <div className="text-xs mb-2" style={{ color: 'var(--sca-muted)' }}>If Floor 5 exits</div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                  Add 12 focus pods on Floor 2 to protect peak experience ($42K one-time)
                </div>
              </div>
            </div>

            {/* Recommended Action + Clarity Stack */}
            <div className="rounded-lg border p-4" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
                Recommended Action to Landlord / CFO
              </h3>
              
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: 'var(--sca-hover)' }}>
                <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>RECOMMENDATION</div>
                <div className="text-sm font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                  Do not renew Floor 5 lease. Consolidate to Floors 1-4.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>Question</div>
                  <p className="text-xs" style={{ color: 'var(--sca-ink)' }}>
                    Can we exit Floor 5 by Jan 2027 lease expiration without peak-day risk?
                  </p>
                </div>

                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>Signal</div>
                  <p className="text-xs" style={{ color: 'var(--sca-ink)' }}>
                    Floor 5 at 38% utilization (8 weeks). Peak Wed 52% vs 85 capacity. 34-week vacancy streak. Floor presence shows sustained emptiness.
                  </p>
                </div>

                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>Decision</div>
                  <p className="text-xs" style={{ color: 'var(--sca-ink)' }}>
                    Consolidate Floor 5 to remaining floors. Reclaim 8,500 sq ft. Add 12 focus pods on Floor 2 for peak protection. Net savings: ${((8500 * leaseRate - 42000) / 1000).toFixed(0)}K/year.
                  </p>
                </div>

                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>Action</div>
                  <p className="text-xs" style={{ color: 'var(--sca-ink)' }}>
                    <strong>Owner:</strong> RE Director<br />
                    <strong>By:</strong> Oct 15, 2026<br />
                    <strong>Next:</strong> Notify landlord, CFO restack memo
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 text-xs" style={{ color: 'var(--sca-muted)' }}>
              Sample building data for illustrative lease-event board pack. Adjust lease rate to match local market.
            </div>
          </div>
        )}

        {/* Secondary: Selected Site Floor Heatmap + Clarity Stack */}
        {selectedSite && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
                {sites.find(s => s.id === selectedSite)?.name}: Floor 3 Snapshot
              </h3>
              <div className="text-xs mb-4" style={{ color: 'var(--sca-muted)' }}>
                Wednesday peak hour view (sample data)
              </div>
              <FloorPlanHeatmap
                zones={sampleZones}
                currentHour={13}
                showScrubber={false}
                width={700}
                height={400}
              />
            </div>

            <div className="rounded-lg border p-6" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
              <div className="mb-4">
                <div className="text-xs font-medium mb-1" style={{ color: '#8a7355' }}>
                  CLARITY STACK
                </div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Riverside Campus Floor 3
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>Question</div>
                  <p className="text-sm" style={{ color: 'var(--sca-ink)' }}>
                    Can we consolidate Riverside Floor 3 before Jan 2027 lease renewal?
                  </p>
                </div>

                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>Signal</div>
                  <p className="text-sm" style={{ color: 'var(--sca-ink)' }}>
                    38% utilization over 8 weeks. Peak Wed 52%, well below capacity. 34-week vacancy streak.
                  </p>
                </div>

                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>Decision</div>
                  <p className="text-sm" style={{ color: 'var(--sca-ink)' }}>
                    Consolidate to Floors 1-2. Reclaim ~8,500 sq ft. Protect Wed peak with added focus seats on Floor 2.
                  </p>
                </div>

                <div>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#8a7355' }}>Action</div>
                  <p className="text-sm" style={{ color: 'var(--sca-ink)' }}>
                    <strong>Owner:</strong> RE Director<br />
                    <strong>By:</strong> Oct 15, 2026<br />
                    <strong>Next:</strong> CFO restack memo
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'var(--sca-badge-bg)' }}>
          <div className="text-sm font-medium mb-2" style={{ color: 'var(--sca-ink)' }}>
            Quick Navigation
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Ghost Meetings', view: 'space utilization' },
              { label: 'Reclaim Scenario', view: 'space optimization' },
              { label: 'Cleaning Routes', view: 'facilities' },
              { label: 'HVAC Setback', view: 'energy' },
              { label: 'After-Hours', view: 'security' }
            ].map((link) => (
              <button
                key={link.view}
                onClick={() => onNavigate(link.view)}
                className="px-3 py-1.5 text-sm rounded-lg transition-colors"
                style={{
                  backgroundColor: 'white',
                  color: 'var(--sca-ink)',
                  border: '1px solid var(--sca-border)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sca-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Healthcare/Retail dashboard unchanged
  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--sca-ink)' }}>Dashboard Overview</h2>
        <p className="text-sm mt-1" style={{ color: 'var(--sca-muted)' }}>Real-time facility monitoring and analytics</p>
      </div>
      <div className="text-center py-12" style={{ color: 'var(--sca-muted)' }}>
        Dashboard content for this segment
      </div>
    </div>
  );
};

export default Dashboard;
